import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Drawer } from 'antd';
import {
  UserOutlined,
  EnvironmentOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  NotificationOutlined,
  AuditOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  FileSearchOutlined,
  MenuOutlined,
  CloseOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { clearSessionPlainPassword } from '../../sessionPlainPassword';
import {
  getRoleLabel,
  getStoredUser,
  hasPerm,
  isSuperAdmin,
} from '../../userAuth';

const menuItems = [
  { label: '用户管理', icon: <UserOutlined />, path: '/admin/users' },
  { label: '存放地点管理', icon: <EnvironmentOutlined />, path: '/admin/locations' },
  { label: '区域管理', icon: <ApartmentOutlined />, path: '/admin/regions' },
  { label: '分区管理', icon: <AppstoreOutlined />, path: '/admin/partitions' },
  { label: '角色类别管理', icon: <SafetyOutlined />, path: '/admin/permissions' },
  { label: '资产管理', icon: <DatabaseOutlined />, path: '/admin/assets' },
  { label: '公告管理', icon: <NotificationOutlined />, path: '/admin/announcements' },
  { label: '审批管理', icon: <AuditOutlined />, path: '/admin/approvals' },
  { label: '操作日志', icon: <FileSearchOutlined />, path: '/admin/op-logs', saOnly: true },
];

function useBreakpoint() {
  const [bp, setBp] = useState(() => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 1100) return 'tablet';
    return 'desktop';
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w < 768) setBp('mobile');
      else if (w < 1100) setBp('tablet');
      else setBp('desktop');
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return bp;
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getStoredUser();
  const canPublishAnnouncement = hasPerm(currentUser, 'publish_announcement');
  const roleText = getRoleLabel(currentUser);
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    try {
      const user = getStoredUser();
      const canPublish = hasPerm(user, 'publish_announcement');
      const isAnnouncementRoute = location.pathname === '/admin/announcements'
        || location.pathname.startsWith('/admin/announcements/');
      const allowByRole = isSuperAdmin(user);
      const allowByPerm = isAnnouncementRoute && canPublish;
      if (!allowByRole && !allowByPerm) {
        navigate('/login', { replace: true });
      }
    } catch {
      navigate('/login', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isTablet) setCollapsed(true);
    if (bp === 'desktop') setCollapsed(false);
  }, [bp, isTablet]);

  const handleLogout = () => {
    localStorage.clear();
    clearSessionPlainPassword();
    navigate('/login');
  };

  const visibleMenus = menuItems.filter((item) => {
    if (item.saOnly && !isSuperAdmin(currentUser)) return false;
    if (!isSuperAdmin(currentUser) && item.path !== '/admin/announcements') return false;
    if (!isSuperAdmin(currentUser) && item.path === '/admin/announcements' && !canPublishAnnouncement) return false;
    return true;
  });

  const go = useCallback((path) => {
    navigate(path);
    setDrawerOpen(false);
  }, [navigate]);

  const renderMenu = (compact = false) => (
    visibleMenus.map((item) => {
      const isActive = location.pathname === item.path
        || location.pathname.startsWith(`${item.path}/`);
      return (
        <div
          key={item.path}
          onClick={() => go(item.path)}
          title={item.label}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 0 : 10,
            justifyContent: compact ? 'center' : 'flex-start',
            padding: compact ? '14px 0' : '14px 20px',
            cursor: 'pointer',
            color: isActive ? '#1890ff' : '#333',
            background: isActive ? '#e6f7ff' : 'transparent',
            borderLeft: compact
              ? 'none'
              : (isActive ? '3px solid #1890ff' : '3px solid transparent'),
            fontSize: 14,
            transition: 'all 0.2s',
          }}
        >
          <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
          {!compact ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span> : null}
        </div>
      );
    })
  );

  const siderWidth = collapsed ? 64 : (isTablet ? 200 : 220);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100dvh',
      maxHeight: '100vh',
      overflow: 'hidden',
      background: '#f5f7fa',
    }}
    >
      {/* Top header */}
      <div
        style={{
          height: 56,
          background: '#fff',
          borderBottom: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '0 12px' : '0 20px',
          flexShrink: 0,
          gap: 8,
          zIndex: 100,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
          {isMobile ? (
            <div
              onClick={() => setDrawerOpen(true)}
              style={{
                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: 18, color: '#333', flexShrink: 0,
              }}
            >
              <MenuOutlined />
            </div>
          ) : (
            <div
              onClick={() => setCollapsed((v) => !v)}
              style={{
                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: 16, color: '#666', flexShrink: 0,
              }}
              title={collapsed ? '展开菜单' : '收起菜单'}
            >
              <MenuOutlined />
            </div>
          )}
          <div
            style={{
              width: 32, height: 32, background: '#1890ff', borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 16, flexShrink: 0,
            }}
          >
            <DatabaseOutlined />
          </div>
          <span style={{
            fontSize: isMobile ? 15 : 16, fontWeight: 600, color: '#333',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
          >
            云仓智管
          </span>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexShrink: 0,
        }}
        >
          {!isMobile ? <Avatar size={32} icon={<UserOutlined />} /> : null}
          {!isMobile ? <span style={{ color: '#333', fontSize: 13 }}>{roleText}</span> : null}
          <span
            onClick={() => navigate('/')}
            style={{
              color: '#1890ff', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <HomeOutlined />
            {isMobile ? '前台' : '返回前台'}
          </span>
          <span
            onClick={handleLogout}
            style={{ color: '#999', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}
          >
            退出
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Desktop / tablet sidebar */}
        {!isMobile ? (
          <div
            style={{
              width: siderWidth,
              background: '#fff',
              borderRight: '1px solid #e8e8e8',
              overflowY: 'auto',
              overflowX: 'hidden',
              flexShrink: 0,
              transition: 'width 0.2s ease',
            }}
          >
            {renderMenu(collapsed)}
          </div>
        ) : null}

        {/* Content */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            background: '#f5f7fa',
            padding: isMobile ? 12 : (isTablet ? 16 : 24),
            overflowY: 'auto',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
          className="admin-content-scroll"
        >
          <div style={{ minWidth: 0, width: '100%', maxWidth: '100%' }}>
            <Outlet />
          </div>
        </div>
      </div>

      {/* Mobile drawer menu */}
      <Drawer
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>后台菜单</span>
            <CloseOutlined onClick={() => setDrawerOpen(false)} style={{ fontSize: 14, color: '#999' }} />
          </div>
        )}
        placement="left"
        open={drawerOpen && isMobile}
        onClose={() => setDrawerOpen(false)}
        width={Math.min(280, typeof window !== 'undefined' ? window.innerWidth * 0.82 : 280)}
        styles={{ body: { padding: 0 } }}
        closable={false}
      >
        <div style={{ padding: '8px 0' }}>
          {renderMenu(false)}
        </div>
        <div
          onClick={() => { setDrawerOpen(false); navigate('/'); }}
          style={{
            margin: '8px 12px',
            padding: '12px 16px',
            borderRadius: 8,
            background: '#e6f7ff',
            color: '#1890ff',
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <HomeOutlined />
          返回前台首页
        </div>
        <div style={{
          padding: '12px 20px 20px', color: '#999', fontSize: 13,
        }}
        >
          {roleText}
        </div>
      </Drawer>

      <style>{`
        .admin-content-scroll .ant-table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .admin-content-scroll .ant-table {
          min-width: 520px;
        }
        .admin-content-scroll .ant-modal {
          max-width: calc(100vw - 24px) !important;
        }
        @media (max-width: 767px) {
          .admin-content-scroll h1,
          .admin-content-scroll h2,
          .admin-content-scroll h3 {
            font-size: 18px !important;
            word-break: break-word;
          }
          .admin-content-scroll .ant-space {
            flex-wrap: wrap !important;
          }
          .admin-content-scroll .ant-btn {
            margin-bottom: 4px;
          }
          .admin-content-scroll .ant-form-inline .ant-form-item {
            margin-right: 8px !important;
            margin-bottom: 8px !important;
          }
          .admin-content-scroll .ant-pagination {
            flex-wrap: wrap;
          }
          .admin-content-scroll .ant-select,
          .admin-content-scroll .ant-input-affix-wrapper {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
